import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, User, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { ROUTES } from '../../lib/constants';

const SignupForm = () => {
  const navigate = useNavigate();
  const { signUp } = useAuth();

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (formData.fullName.trim().length < 2) {
      newErrors.fullName = 'Name must be at least 2 characters';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);

    try {
      await signUp(formData.email, formData.password, formData.fullName);
      navigate(ROUTES.LOGIN, {
        state: { message: "Account created! Please check your email to verify. If you don't see it in your Inbox, please check your Spam folder." }
      });
    } catch (error) {
      console.error('Signup failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto px-4 sm:px-6 md:px-8">
      {/* Glassmorphism card - responsive */}
      <div className="backdrop-blur-lg bg-white/40 border border-cream-300/50 rounded-2xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 md:p-10">
        
        {/* Logo/Brand */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="flex justify-center mb-6 sm:mb-8">
            <div className="rounded-full bg-gradient-to-br from-brown-500 to-brown-700 border-4 border-brown-100 shadow-lg flex items-center justify-center aspect-square overflow-hidden w-16 sm:w-18 md:w-20 lg:w-24">
              <img 
                src="/book.png" 
                alt="OmniClarix - Clarity, Simplified." 
                className="w-auto h-4/5 object-contain pointer-events-none"
              />
            </div>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-brown-900 mb-1 sm:mb-2">
            Join OmniClarix
          </h2>
          <p className="text-sm sm:text-base text-brown-600">
            Start simplifying your learning today
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4 md:space-y-5">
          {/* Full Name Field */}
          <div>
            <label htmlFor="fullName" className="block text-sm sm:text-base font-medium text-brown-800 mb-1.5 sm:mb-2">
              Full Name
            </label>
            <div className="relative">
              <User className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-brown-400 w-4 h-4 sm:w-5 sm:h-5" />
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Ola Timi"
                className={`w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 md:py-3.5 
                  bg-white/60 backdrop-blur-sm border-2 rounded-lg sm:rounded-xl 
                  focus:ring-2 focus:ring-brown-500 focus:border-brown-500 
                  outline-none transition-all text-sm sm:text-base
                  placeholder:text-brown-300 text-brown-900
                  ${errors.fullName ? 'border-red-400' : 'border-cream-300'}`}
                disabled={loading}
              />
            </div>
            {errors.fullName && (
              <p className="mt-1 sm:mt-1.5 text-xs sm:text-sm text-red-600">{errors.fullName}</p>
            )}
          </div>

          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-sm sm:text-base font-medium text-brown-800 mb-1.5 sm:mb-2">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-brown-400 w-4 h-4 sm:w-5 sm:h-5" />
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                className={`w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 md:py-3.5 
                  bg-white/60 backdrop-blur-sm border-2 rounded-lg sm:rounded-xl 
                  focus:ring-2 focus:ring-brown-500 focus:border-brown-500 
                  outline-none transition-all text-sm sm:text-base
                  placeholder:text-brown-300 text-brown-900
                  ${errors.email ? 'border-red-400' : 'border-cream-300'}`}
                disabled={loading}
              />
            </div>
            {errors.email && (
              <p className="mt-1 sm:mt-1.5 text-xs sm:text-sm text-red-600">{errors.email}</p>
            )}
          </div>

          {/* Password Field */}
          <div>
            <label htmlFor="password" className="block text-sm sm:text-base font-medium text-brown-800 mb-1.5 sm:mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-brown-400 w-4 h-4 sm:w-5 sm:h-5" />
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Min. 6 characters"
                className={`w-full pl-10 sm:pl-12 pr-10 sm:pr-12 py-2.5 sm:py-3 md:py-3.5 
                  bg-white/60 backdrop-blur-sm border-2 rounded-lg sm:rounded-xl 
                  focus:ring-2 focus:ring-brown-500 focus:border-brown-500 
                  outline-none transition-all text-sm sm:text-base
                  placeholder:text-brown-300 text-brown-900
                  ${errors.password ? 'border-red-400' : 'border-cream-300'}`}
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 text-brown-400 hover:text-brown-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" /> : <Eye className="w-4 h-4 sm:w-5 sm:h-5" />}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 sm:mt-1.5 text-xs sm:text-sm text-red-600">{errors.password}</p>
            )}
          </div>

          {/* Confirm Password Field */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm sm:text-base font-medium text-brown-800 mb-1.5 sm:mb-2">
              Confirm Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-brown-400 w-4 h-4 sm:w-5 sm:h-5" />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Re-enter password"
                className={`w-full pl-10 sm:pl-12 pr-10 sm:pr-12 py-2.5 sm:py-3 md:py-3.5 
                  bg-white/60 backdrop-blur-sm border-2 rounded-lg sm:rounded-xl 
                  focus:ring-2 focus:ring-brown-500 focus:border-brown-500 
                  outline-none transition-all text-sm sm:text-base
                  placeholder:text-brown-300 text-brown-900
                  ${errors.confirmPassword ? 'border-red-400' : 'border-cream-300'}`}
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 text-brown-400 hover:text-brown-600"
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" /> : <Eye className="w-4 h-4 sm:w-5 sm:h-5" />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="mt-1 sm:mt-1.5 text-xs sm:text-sm text-red-600">{errors.confirmPassword}</p>
            )}
          </div>

          {/* Terms Checkbox */}
          <div>
            <label className="flex items-start space-x-2 sm:space-x-3">
              <div className="relative flex items-center mt-0.5">
                <input
                  type="checkbox"
                  name="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={handleChange}
                  className="w-4 h-4 sm:w-5 sm:h-5 text-brown-600 border-2 border-cream-300 rounded focus:ring-brown-500 cursor-pointer"
                />
              </div>
              <span className="text-xs sm:text-sm text-brown-700 leading-tight">
                I agree to the{' '}
                <Link to="/terms" className="text-brown-800 font-semibold hover:underline">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-brown-800 font-semibold hover:underline">
                  Privacy Policy
                </Link>
              </span>
            </label>
            {errors.agreeToTerms && (
              <p className="mt-1 sm:mt-1.5 text-xs sm:text-sm text-red-600">{errors.agreeToTerms}</p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-brown-600 to-brown-700 hover:from-brown-700 hover:to-brown-800 
              text-white font-medium py-2.5 sm:py-3 md:py-3.5 px-4 rounded-lg sm:rounded-xl 
              transition-all duration-200 flex items-center justify-center 
              disabled:opacity-50 disabled:cursor-not-allowed shadow-lg
              text-sm sm:text-base md:text-lg mt-4 sm:mt-6"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 mr-2 animate-spin" />
                <span>Creating account...</span>
              </>
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        {/* Login Link */}
        <div className="mt-6 sm:mt-8 text-center">
          <p className="text-xs sm:text-sm text-brown-600">
            Already have an account?{' '}
            <Link
              to={ROUTES.LOGIN}
              className="text-brown-700 hover:text-brown-900 font-semibold transition-colors"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignupForm;